import type { ReactNode } from "react";

import {
  QuietLine,
  WithdrawnNotice,
} from "@/components/courses/workspace/WorkspacePrimitives";
import { useRegistrarWorkspaceFrame } from "@/components/registrar/use-registrar-workspace-frame";
import { Skeleton } from "@/components/ui/skeleton";
import { Tile } from "@/components/ui/tile";
import { useTileHeadingLevel } from "@/components/ui/tile-heading-context";
import { cn } from "@/lib/utils";

interface AcademicPageHeaderProps {
  /** Omitted for a page with no place in the sidebar; see `useWorkspaceEyebrow`. */
  eyebrow?: string;
  title: string;
  description: string;
  titleAccessory?: ReactNode;
  actions?: ReactNode;
}

export function AcademicPageHeader({
  eyebrow,
  title,
  description,
  titleAccessory,
  actions,
}: AcademicPageHeaderProps) {
  const inRegistrarWorkspace = useRegistrarWorkspaceFrame();
  const Heading = inRegistrarWorkspace ? "h2" : "h1";

  return (
    <header data-slot="page-frame" className="grid min-w-0 items-end gap-5 lg:grid-cols-[minmax(0,1fr)_auto]">
      <div className="grid min-w-0 gap-2">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <div className="flex min-w-0 items-center gap-2.5">
          <Heading
            className={cn(
              "max-w-[22ch] font-semibold text-foreground",
              inRegistrarWorkspace
                ? "text-tile-title"
                : "text-title",
            )}
          >
            {title}
          </Heading>
          {titleAccessory && <div className="shrink-0">{titleAccessory}</div>}
        </div>
        <p className="max-w-2xl text-ui text-muted-foreground">
          {description}
        </p>
      </div>
      {actions && <div className="flex min-w-0 flex-wrap items-center gap-2 lg:justify-end">{actions}</div>}
    </header>
  );
}

export interface SummaryItem {
  label: string;
  value: string | number;
  detail?: string;
}

/**
 * Column tracks per item count. Literal class strings because Tailwind reads
 * source text, not runtime values — an interpolated `grid-cols-${n}` compiles
 * to nothing. Counts above six fall back to the four-column arrangement and
 * wrap, which the separator rules below handle without knowing the row count.
 */
const SUMMARY_COLUMNS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-2 lg:grid-cols-4",
  5: "grid-cols-2 lg:grid-cols-5",
  6: "grid-cols-2 lg:grid-cols-3 xl:grid-cols-6",
};

/**
 * The glance row of a record page: one stat tile per figure, on the bento gap.
 *
 * It used to be a bordered strip with per-cell separators clipped by a frame.
 * Under ADR 0029 a figure is a tile like every other surface: tenant surface,
 * 16 px radius, 1 px line, no shadow. The count-independent column map stays so
 * three, four, five, or six figures all lay out without a stray divider.
 */
export function AcademicSummaryStrip({ items, label }: { items: SummaryItem[]; label: string }) {
  if (items.length === 0) return null;

  return (
    <section
      aria-label={label}
      data-workspace-weight="support"
      className={cn("grid gap-[var(--layout-bento-gap)]", SUMMARY_COLUMNS[items.length] ?? SUMMARY_COLUMNS[4])}
    >
      {items.map((item) => (
        <Tile key={item.label} as="div" className="min-h-[5.5rem] justify-end gap-0.5">
          <span className="text-figure font-semibold tabular-nums text-foreground">{item.value}</span>
          <span className="min-w-0 break-words text-meta font-semibold text-foreground">{item.label}</span>
          {item.detail && <span className="break-words text-meta text-muted-foreground">{item.detail}</span>}
        </Tile>
      ))}
    </section>
  );
}

interface DataWorkspaceProps {
  title: string;
  description?: string;
  resultLabel?: string;
  tools?: ReactNode;
  filters?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}

export function DataWorkspace({
  title,
  description,
  resultLabel,
  tools,
  filters,
  children,
  className,
  contentClassName,
}: DataWorkspaceProps) {
  const Heading = useTileHeadingLevel();
  return (
    <Tile
      aria-label={title}
      data-workspace-weight="support"
      className={cn("gap-0 overflow-hidden p-0", className)}
    >
      <header className="grid gap-4 py-4 ps-5 pe-4 sm:grid-cols-[minmax(0,1fr)_auto]">
        <div className="grid min-w-0 gap-1">
          <Heading className="text-section font-semibold">{title}</Heading>
          {description && <p className="text-ui text-muted-foreground">{description}</p>}
        </div>
        {tools && <div className="min-w-0 sm:min-w-64">{tools}</div>}
      </header>
      <div className="border-t border-rule">
        {(filters || resultLabel) && (
          <div className="flex flex-col gap-3 py-3 ps-5 pe-4">
            {filters}
            {resultLabel && <p className="text-meta text-muted-foreground" aria-live="polite">{resultLabel}</p>}
          </div>
        )}
        <div className={cn("min-w-0 px-0", contentClassName)}>{children}</div>
      </div>
    </Tile>
  );
}

interface WorkspaceStateProps {
  kind: "loading" | "error" | "empty";
  title?: string;
  description: string;
  onRetry?: () => void;
  className?: string;
}

export function WorkspaceState({ kind, title, description, onRetry, className }: WorkspaceStateProps) {
  if (kind === "loading") {
    return (
      <div className={cn("grid gap-3 px-4 py-5 sm:px-6", className)} aria-label={description} role="status">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-4/5" />
      </div>
    );
  }

  const sentence = title
    ? `${title}${/[.!?]$/.test(title.trim()) ? "" : "."} ${description}`
    : description;

  if (kind === "error") {
    return (
      <WithdrawnNotice onRetry={onRetry} className={cn("px-4 py-5 sm:px-6", className)}>
        {sentence}
      </WithdrawnNotice>
    );
  }

  return (
    <QuietLine announce className={cn("px-4 py-5 sm:px-6", className)}>
      {sentence}
    </QuietLine>
  );
}
